import { AfterViewInit, Component, EventEmitter, Inject, Input, Output, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';

import { environment } from '../../../environment';
import { ILoggingService } from '../../../services/interfaces/ILoggingService';
import { LayerService } from '../../../services/layer.service';
import { SpatialQueryService } from '../../../services/spatialQuery.service';
import { LogLevel } from '../../../types';
import { BaseComponent } from '../../base.component';

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-layer-spatial-query',
    styleUrls: ['../../sharedStyles/base.scss', '../../sharedStyles/telerik.scss', './layer-spatial-query.component.scss'],
    templateUrl: './layer-spatial-query.component.html'
})
export class LayerSpatialQueryComponent extends BaseComponent implements AfterViewInit {
    constructor(_vcr: ViewContainerRef,
        private readonly layerService: LayerService,
        private readonly spatialQueryService: SpatialQueryService,
        @Inject('LoggingService') public LoggingService: ILoggingService
    ) {
        super(_vcr);
    }
    @Input() primaryLayers: any[];
    @Input() joinedLayers: any[];
    @Input() mapView: NonNullable<__esri.MapView>;
    @Input() @ViewChild('geographicFilter') geographicFilter;
    @Input() get getSpatialQueryService() {
        return this.spatialQueryService;
    }
    @Output() public readonly onboundarysourcechanged = new EventEmitter<string>();
    @Output() public readonly onapply = new EventEmitter<{ isNewLayer: boolean; results: { id: string; guid?: string; objectIds?: number[]; resultCount?: number }[] }>();

    showLoader: boolean;
    primaryDynamicLayers: any[] = [];
    joinedDynamicLayers: any[] = [];
    primaryFeatureLayers: any[] = [];
    joinedFeatureLayers: any[] = [];
    joinedGeometries: any[] = [];
    primaryLayerList: any = [];
    joinedLayerList: any = [];
    tempJoinedList: any = [];
    selectedPrimaryLayers: any[] = [];
    selectedJoinedLayers: any[];
    isIntersect: boolean;
    srMatch = true;
    newLayer: boolean;
    disable: boolean;
    filteredGeometryString = '';
    hideValues = ['4', '5', '6'];
    showValueInOption = false;
    drawBoundary = false;
    //chosenMulitSelect: SelectOptionData[] = [];
    multiSelect = false;
    graphicsObject: any[] = [];
    boundaryType = '';
    processingType = 'Default';
    geoFilterOnApplySub: Subscription;
    geoFilterOnDraweSub: Subscription;
    geoFilterOnDrawCompleteSub: Subscription;
    onfeatureschangedSub: Subscription;
    results: { id: string; guid?: string; objectIds?: number[]; resultCount?: number }[];
    queryOptions: any[] = [{
        value: true,
        text: 'Intersect'
    }, {
        value: false,
        text: 'Difference'
    }];
    layerOptions: any[] = [{
        value: true,
        text: 'Create New Layer(s)'
    }, {
        value: false,
        text: 'Update Layer(s)'
    }];
    windowRef: any;

    async ngAfterViewInit() {
        this.isReady();
        const mapRef = await this.getClosestAncestor('mc-map');
        await mapRef.ready;
        //this.mapView = mapRef.mapView;

        this.mapView = await this.setupMapViewRef(this.mapView);
        this.geographicFilter.containerElement = this.hostElement;
        this.geographicFilter.onboundarysourcechanged.subscribe(source => {
            if (source) {
                this.reset();
            }
            this.onboundarysourcechanged.emit(source);
        });
        this.windowRef = await this.getClosestAncestor('mc-window');

        this.windowRef.addEventListener('openChange', open => {
            if (!open.detail) {
                this.close(true);
            } else if (!this.geographicFilter.isActivated) {
                this.geographicFilter.activate();
            }
        });

        if (!this.geoFilterOnDraweSub) {
            this.geoFilterOnDraweSub = this.geographicFilter.ondraw.subscribe(() => {
                this.boundaryType = '';
                this.windowRef.hidden = true;
            });
        }
        if (!this.geoFilterOnDrawCompleteSub) {
            this.geoFilterOnDrawCompleteSub = this.geographicFilter.ondrawcomplete.subscribe(data => {
                this.filteredGeometryString = JSON.stringify(data[0]);
                this.setupGraphics(this.getGeometryFromPolygon(data[0]));
                this.windowRef.hidden = false;
            });
        }
        if (!this.onfeatureschangedSub) {
            this.onfeatureschangedSub = this.geographicFilter.onfeatureschanged.subscribe(async data => {
                const geometryString = data.geometryString;
                this.boundaryType = data.boundaryType ? data.boundaryType.displayText : '';
                if (geometryString !== null) {
                    this.filteredGeometryString = geometryString;
                    this.setupGraphics(this.getGeometryFromPolygon(JSON.parse(geometryString)));
                } else {
                    this.graphicsObject = [];
                    this.filteredGeometryString = '';
                }
            });
        }

    }
    public getGeometryFromPolygon(polygonGeometry: { rings: number[][][] }) {
        return { rings: polygonGeometry.rings, type: 'polygon', spatialReference: this.layerService.mapView.spatialReference };
    }

    public setupGraphics(geometry: any) {
        this.graphicsObject = [{
            geometry,
            symbol: {
                type: 'simple-fill',
                color: [0, 0, 0, 0],
                style: 'solid',
                outline: {
                    style: 'solid',
                    color: [0, 0, 0, 1],
                    width: 3
                }
            }
        }];
        if (this.graphicsObject.length === 0 && geometry === null) {
            this.disable = true;
        } else {
            this.disable = false;
        }
    }
    public async runSpatialQuery() {
        try {
            this.disable = true;
            this.showLoader = true;
            if (!(this.selectedJoinedLayers && this.selectedJoinedLayers.length > 0 && this.graphicsObject.length === 0)) { //boundary spatial query
                this.joinedGeometries = [this.filteredGeometryString];
            }
            let dynamicResults: { id: string; guid: string; resultCount?: number }[] = [];
            let featureResults: { id: string; objectIds: number[]; resultCount?: number }[] = [];
            if (this.primaryDynamicLayers && this.primaryDynamicLayers.length && this.primaryDynamicLayers.length > 0) {
                dynamicResults = await this.spatialQueryService.spatialQuery(this.isIntersect, this.primaryDynamicLayers, [], this.joinedDynamicLayers, this.joinedFeatureLayers, this.joinedGeometries, this.processingType, this.srMatch);
            }
            if (this.primaryFeatureLayers && this.primaryFeatureLayers.length && this.primaryFeatureLayers.length > 0) {
                featureResults = await this.spatialQueryService.spatialQuery(this.isIntersect, [], this.primaryFeatureLayers, this.joinedDynamicLayers, this.joinedFeatureLayers, this.joinedGeometries, this.processingType, this.srMatch, true);
            }
            this.results = [...dynamicResults, ...featureResults];
            const spatialQueryParams = {
                primaryDynamicLayers: [],
                primaryFeatureLayers: [],
                joinedDynamicLayers: this.joinedDynamicLayers,
                joinedFeatureLayers: this.joinedFeatureLayers,
                joinedGeometries: this.joinedGeometries,
                isIntersect: this.isIntersect,
                guid: '',
                processingType: this.processingType,
                srMatch: this.srMatch
            };
            await this.spatialQueryService.handleResults(this.results, spatialQueryParams, this.newLayer, this.isIntersect);
            //add border
            if (this.drawBoundary && this.graphicsObject && this.graphicsObject.length > 0) {
                const graphicsProperties = {
                    graphics: this.graphicsObject,
                    title: this.boundaryType ? this.boundaryType : 'Selected Area'
                };
                this.layerService.addGraphicsLayer(graphicsProperties);
            }
        } catch (error) {
            this.LoggingService.Log(error, LogLevel.Error);
        } finally {
            this.geographicFilter.boundarySource = 'system';
            this.disable = false;
            this.showLoader = false;
            this.onapply.emit({ isNewLayer: this.newLayer, results: this.results });
            this.reset();
            this.close();
        }
    }

    setPrimaryLayers() {
        this.primaryDynamicLayers = [];
        this.primaryFeatureLayers = [];
        this.spatialQueryService.filterSelectedLayers(this.selectedPrimaryLayers, this.primaryDynamicLayers, this.primaryFeatureLayers);
        this.joinedLayerList = this.tempJoinedList;
        this.selectedPrimaryLayers.forEach(layer => {
            this.joinedLayerList = this.joinedLayerList.filter(l => l.id !== layer.id);
            this.selectedJoinedLayers = this.selectedJoinedLayers.filter(l => l.id !== layer.id);
        });
        if (this.selectedPrimaryLayers.length === 0 || this.selectedJoinedLayers.length === 0) {
            this.disable = true;
        } else {
            this.disable = false;
        }
    }

    setJoinedLayers() {
        this.joinedFeatureLayers = [];
        this.joinedDynamicLayers = [];
        this.joinedGeometries = [];
        this.spatialQueryService.filterSelectedLayers(this.selectedJoinedLayers, this.joinedDynamicLayers, this.joinedFeatureLayers, this.joinedGeometries);
        if (this.selectedPrimaryLayers.length === 0 || this.selectedJoinedLayers.length === 0) {
            this.disable = true;
        } else {
            this.disable = false;
        }
    }
    setProcessingType(type) {
        this.processingType = type;
    }

    @Input()
    reset = () => {
        this.isIntersect = true;
        this.newLayer = true;
        this.selectedPrimaryLayers = [];
        this.selectedJoinedLayers = [];
        this.graphicsObject = [];
        this.filteredGeometryString = '';
        this.srMatch = true;
        this.disable = false;
        this.windowRef.hidden = false;
        this.drawBoundary = false;
        this.geographicFilter.runSpatialQueryOnSketchComplete = false;
        this.geographicFilter.spatialQuerySelection = true;
        this.geographicFilter.containerElement = this.hostElement;
        this.processingType = 'Default';
        if (this.primaryLayers && this.primaryLayers.length > 0) {
            this.primaryLayerList = this.spatialQueryService.GetMapLayerList(this.primaryLayers, true).reverse();
            this.joinedLayerList = this.spatialQueryService.GetMapLayerList(this.joinedLayers).reverse();
            this.tempJoinedList = this.joinedLayerList;
            this.selectedPrimaryLayers = this.primaryLayerList;
            this.setPrimaryLayers();
        } else {
            const layers = this.layerService.mapRef.layers.toArray();
            this.primaryLayerList = this.spatialQueryService.GetMapLayerList(layers, true).reverse();
            this.joinedLayerList = this.spatialQueryService.GetMapLayerList(layers).reverse();
            this.tempJoinedList = this.joinedLayerList;
        }
    }

    close(ignoreWindow = false) {
        this.drawBoundary = false;
        this.showValueInOption = false;
        this.graphicsObject = [];
        this.filteredGeometryString = '';
        this.geographicFilter.resetComponent();
        if (!ignoreWindow) {
            this.windowRef.close();
        }
    }

    sortStringObj(sortBy: 'text' | 'value', obj: { text: string; value: string }[]) {
        obj.sort((a, b) => {
            if (a[sortBy].localeCompare(b[sortBy]) > 0) {
                return 1;
            } else if (a[sortBy].localeCompare(b[sortBy]) < 0) {
                return -1;
            }
            return 0;
        });
    }

    getLayerIdByName(name: string) {
        return environment.layers.filter(x => x.name === name)[0].id;
    }
}
